import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { EmptyState, Panel, RouteLine, Skeleton, StatusBadge, T, btnSecondary, inputCls, money } from "@/components/admin/ui";

export default function AdminDispatch() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [assignDriver, setAssignDriver] = useState("");

  const load = useCallback(() => {
    adminApi.get("/dispatch", { params: { date } }).then((r) => setData(r.data)).catch(() => {});
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const shift = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  const assign = async () => {
    if (!selected || !assignDriver) return;
    try {
      const r = await adminApi.post(`/bookings/${selected.id}/assign`, { driver_id: assignDriver });
      if (r.data.conflict) {
        toast.error("Conflit horaire", { description: r.data.conflicts.map((c) => `${c.tracking_code} à ${c.time}`).join(" · ") });
        return;
      }
      toast.success("Chauffeur attribué");
      setSelected(null); setAssignDriver("");
      load();
    } catch (error) {
      toast.error(apiError(error, "Attribution impossible"));
    }
  };

  const rides = data?.rides || [];
  const unassigned = rides.filter((r) => !r.driver_id && r.display_status !== "cancelled" && r.status === "booked");
  const live = rides.filter((r) => ["driver_on_the_way", "driver_arrived", "on_board"].includes(r.status));

  return (
    <div className="space-y-4" data-testid="admin-dispatch">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Dispatch</h1>
          <p className="mt-0.5 text-sm text-slate">Le temps d’abord : courses du jour, chauffeurs, conflits.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => shift(-1)} className={btnSecondary} data-testid="dispatch-prev" aria-label="Jour précédent"><ChevronLeft className="h-4 w-4" /></button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dispatch-date" className={`${inputCls} !w-32 font-mono sm:!w-40`} />
          <button type="button" onClick={() => shift(1)} className={btnSecondary} data-testid="dispatch-next" aria-label="Jour suivant"><ChevronRight className="h-4 w-4" /></button>
          <button type="button" onClick={() => setDate(new Date().toISOString().slice(0, 10))} className={btnSecondary} data-testid="dispatch-today">Aujourd’hui</button>
        </div>
      </div>

      {(data?.conflicts || []).length > 0 && (
        <div className="flex items-center gap-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="dispatch-conflicts">
          <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          {data.conflicts.length} conflit(s) horaire : {data.conflicts.map((c) => c.rides.join(" ↔ ")).join(" · ")}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <Panel title={`Timeline — ${date} (${rides.length} courses)`} testId="dispatch-timeline">
            {rides.length === 0 && <p className="py-8 text-center text-sm text-slate">Aucune course ce jour.</p>}
            <div className="relative space-y-1.5">
              <div className="absolute bottom-2 left-16 top-2 hidden w-px bg-slate/15 sm:block" />
              {rides.map((r) => (
                <div key={r.id} onClick={() => { setSelected(r); setAssignDriver(r.driver_id || ""); }}
                  data-testid={`dispatch-ride-${r.tracking_code}`}
                  className={`relative flex cursor-pointer items-center gap-3 rounded border px-3 py-2.5 text-sm transition-colors ${
                    selected?.id === r.id ? "border-miles bg-miles-ice/60" : "border-slate/15 bg-white hover:border-miles/40"}`}>
                  <span className="w-14 shrink-0 font-mono text-sm font-semibold text-ink">{r.time}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink">{r.pickup} → {r.dropoff || "à l’heure"}</span>
                    <span className="block truncate text-xs text-slate">{r.customer?.first_name} {r.customer?.last_name} · {r.vehicle_category} · {r.duration_min} min</span>
                  </span>
                  <span className="hidden shrink-0 text-xs text-slate sm:block">{r.driver?.first_name || "—"}</span>
                  <StatusBadge status={r.display_status} />
                </div>
              ))}
            </div>
          </Panel>

          {live.length > 0 && (
            <Panel title={`En cours maintenant (${live.length})`} testId="dispatch-live">
              {live.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b border-slate/10 py-2 text-sm last:border-0">
                  <span className="font-mono font-semibold text-miles">{r.tracking_code}</span>
                  <span className="text-ink">{r.driver?.first_name} · {r.customer?.first_name} {r.customer?.last_name}</span>
                  <StatusBadge status={r.display_status} />
                </div>
              ))}
            </Panel>
          )}
        </div>

        <div className="space-y-4 lg:col-span-4">
          <Panel title={`À attribuer (${unassigned.length})`} testId="dispatch-unassigned">
            {unassigned.length === 0 && <p className="py-3 text-sm text-emerald-600" data-testid="unassigned-ok">Toutes les courses ont un chauffeur.</p>}
            {unassigned.map((r) => (
              <button key={r.id} type="button" onClick={() => { setSelected(r); setAssignDriver(""); }} data-testid={`dispatch-unassigned-${r.tracking_code}`}
                className="mb-2 block w-full rounded border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm transition-colors hover:border-amber-400">
                <span className="font-mono font-semibold text-ink">{r.time}</span>
                <span className="ml-2 text-ink">{r.pickup} → {r.dropoff || "à l’heure"}</span>
                <span className="block font-mono text-xs text-slate">{money(r.price_chf)}</span>
              </button>
            ))}
          </Panel>

          {selected && (
            <Panel title={`Attribuer — ${selected.tracking_code}`} testId="dispatch-assign">
              <p className="mb-3 text-sm text-slate">{selected.time} · {selected.pickup} → {selected.dropoff || "à l’heure"} · {selected.duration_min} min</p>
              <select value={assignDriver} onChange={(e) => setAssignDriver(e.target.value)} data-testid="dispatch-driver-select" className={inputCls}>
                <option value="">— chauffeur —</option>
                {(data?.drivers || []).map((d) => (
                  <option key={d.id} value={d.id} disabled={d.status === "suspended" || d.status === "offline"}>
                    {d.first_name} {d.last_name} — {({available: "disponible", busy: "occupé", offline: "hors ligne", suspended: "suspendu"})[d.status] || d.status}{d.rides_today.length > 0 ? ` · ${d.rides_today.length} course(s) ce jour` : ""}
                  </option>
                ))}
              </select>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={assign} disabled={!assignDriver} className="inline-flex h-9 flex-1 items-center justify-center rounded bg-miles px-4 text-sm font-semibold text-white hover:bg-[#1c45cc] disabled:opacity-50" data-testid="dispatch-assign-button">
                  Attribuer
                </button>
                <button type="button" onClick={() => navigate(`/admin/app/bookings/${selected.id}`)} className={btnSecondary} data-testid="dispatch-open-booking">
                  Fiche
                </button>
              </div>
            </Panel>
          )}

          <Panel title="Chauffeurs" testId="dispatch-drivers">
            {(data?.drivers || []).length === 0 && <p className="py-3 text-sm text-slate">Aucun chauffeur créé.</p>}
            {(data?.drivers || []).map((d) => (
              <div key={d.id} className="flex items-center justify-between border-b border-slate/10 py-2 text-sm last:border-0">
                <span className="font-medium text-ink">{d.first_name} {d.last_name}</span>
                <span className={`rounded border px-2 py-0.5 text-xs font-medium ${
                  d.status === "available" || d.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : d.status === "busy" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate/20 bg-cloud text-slate"}`}>
                  {d.status}{d.rides_today.length > 0 ? ` · ${d.rides_today.length}` : ""}
                </span>
              </div>
            ))}
          </Panel>

          <Panel title="Véhicules" testId="dispatch-vehicles">
            {(data?.vehicles || []).length === 0 && <p className="py-3 text-sm text-slate">Aucun véhicule créé.</p>}
            {(data?.vehicles || []).map((v) => (
              <div key={v.id} className="flex items-center justify-between border-b border-slate/10 py-2 text-sm last:border-0">
                <span className="text-ink">{v.make} {v.model}</span>
                <span className="font-mono text-xs text-slate">{v.plate}</span>
                <span className={`rounded border px-2 py-0.5 text-xs font-medium ${v.status === "available" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate/20 bg-cloud text-slate"}`}>{v.status}</span>
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
